/**
 * The person's tickets (TMMAPP-0142 U2 items 13, 25d; TMMAPI-0181 U5, MM-D-147): support cases and
 * feature requests, free and never AI. Wire types from `src/api/generated/`, normalized into the
 * module's domain types.
 *
 * - **Numbers:** the server's bare number (`1001`) is the path segment; the app composes the label
 *   from the type (`SC-1001`, `FR-1001`). A prefixed number in the path is a 404.
 * - **Filing:** `POST /v1/me/tickets {type, title, description, draftTurnId?, attachmentIds?}`: the
 *   attachments are the caller's own assistant uploads, copied into the ticket. At most 10 a day
 *   (`429 RATE_LIMITED`).
 * - **A reply** carries its own attachments the same way; **Close** is for a support case only.
 * - **A duplicate** names its canonical ticket (`duplicateOf`) and that ticket's public status.
 * - A `STAFF` update has no author name: it is from the support team.
 */
import { download, request, type DownloadResult } from './client';
import type {
  MemberTicket as WireTicket,
  MemberTicketUpdateRequest,
  TicketAttachment as WireTicketAttachment,
  TicketCreateRequest,
  TicketUpdate as WireTicketUpdate,
} from './generated';
import type { TicketType } from './assistant';

export type { TicketType } from './assistant';

/** A ticket's public status, as the contract spells it. */
export type TicketStatus = WireTicket['status'];
export type TicketUpdateAuthor = 'MEMBER' | 'STAFF' | 'SYSTEM';

export interface TicketAttachment {
  attachmentId: number;
  name: string;
  contentType: string;
  /** `IMAGE`, `PDF`, `SHEET`, … verbatim. */
  kind: string;
  sizeBytes: number;
}

export interface TicketUpdate {
  id: number;
  authorKind: TicketUpdateAuthor;
  /** MEMBER: the requester's name; null for STAFF ("the support team") and SYSTEM. */
  authorName: string | null;
  body: string;
  createdAt: string;
  /** A status change's SYSTEM update: the status it moved to. */
  toStatus: TicketStatus | null;
  attachments: TicketAttachment[];
}

export interface Ticket {
  number: number;
  type: TicketType;
  title: string;
  description: string;
  status: TicketStatus;
  createdAt: string;
  updatedAt: string;
  /** The canonical ticket this one duplicates, and its public status; null when not a duplicate. */
  duplicateOf: number | null;
  duplicateOfStatus: TicketStatus | null;
  /** The assistant turn it was drafted in, if any. */
  draftTurnId: number | null;
  /** The release it shipped in (a feature request); null when not served. */
  shippedIn: string | null;
  attachments: TicketAttachment[];
  /** On the ticket view only: the public updates, oldest first (empty on the list). */
  updates: TicketUpdate[];
}

export interface TicketCreate {
  type: TicketType;
  title: string;
  description: string;
  draftTurnId?: number | undefined;
  attachmentIds?: readonly number[] | undefined;
}

/** The longest title and description the server accepts. */
export const TICKET_TITLE_MAX = 120;
export const TICKET_DESCRIPTION_MAX = 4000;

/** `SC-1001` or `FR-1001`: the number people see, composed from the type. */
export function ticketLabel(ticket: Pick<Ticket, 'type' | 'number'>): string {
  return `${ticket.type === 'FEATURE_REQUEST' ? 'FR' : 'SC'}-${ticket.number}`;
}

/** A ticket number from a link (`1001`, `SC-1001`, `FR-1001`); null when it isn't one. */
export function parseTicketNumber(raw: string | null | undefined): number | null {
  const match = /^(?:(?:SC|FR)-)?(\d{1,12})$/i.exec((raw ?? '').trim());
  return match === null ? null : Number(match[1]);
}

function normalizeAttachment(w: WireTicketAttachment): TicketAttachment {
  return {
    attachmentId: w.attachmentId,
    name: w.name,
    contentType: w.contentType,
    kind: w.kind,
    sizeBytes: w.sizeBytes,
  };
}

function normalizeUpdate(w: WireTicketUpdate): TicketUpdate {
  const authorKind: TicketUpdateAuthor =
    w.authorKind === 'STAFF' || w.authorKind === 'SYSTEM' ? w.authorKind : 'MEMBER';
  return {
    id: w.id,
    authorKind,
    // A staff person's name is never shown to a member, even if one were served.
    authorName: authorKind === 'MEMBER' ? (w.authorName ?? null) : null,
    body: w.body,
    createdAt: w.createdAt,
    toStatus: w.toStatus ?? null,
    attachments: (w.attachments ?? []).map(normalizeAttachment),
  };
}

export function normalizeTicket(w: WireTicket): Ticket {
  return {
    number: w.number,
    type: w.type === 'FEATURE_REQUEST' ? 'FEATURE_REQUEST' : 'SUPPORT_CASE',
    title: w.title,
    description: w.description,
    status: w.status,
    createdAt: w.createdAt,
    updatedAt: w.updatedAt,
    duplicateOf: w.duplicateOf ?? null,
    duplicateOfStatus: w.duplicateOfStatus ?? null,
    draftTurnId: w.draftTurnId ?? null,
    shippedIn: w.shippedIn ?? null,
    attachments: (w.attachments ?? []).map(normalizeAttachment),
    // Public updates only reach a member; an internal one is never shown even if served.
    updates: (w.updates ?? []).filter((u) => u.visibility !== 'INTERNAL').map(normalizeUpdate),
  };
}

const BASE = '/v1/me/tickets';

export const ticketsApi = {
  /** GET /v1/me/tickets?type= — the caller's own tickets. */
  async list(type?: TicketType): Promise<Ticket[]> {
    const wire = await request<WireTicket[]>('GET', BASE, {
      ...(type !== undefined ? { query: { type } } : {}),
    });
    return wire.map(normalizeTicket);
  },
  /** GET /v1/me/tickets/{number} — with its public updates. */
  async get(number: number): Promise<Ticket> {
    return normalizeTicket(await request<WireTicket>('GET', `${BASE}/${number}`));
  },
  /** POST /v1/me/tickets — `draftTurnId` and `attachmentIds` only when there are some. */
  async create(body: TicketCreate): Promise<Ticket> {
    const json: TicketCreateRequest = {
      type: body.type,
      title: body.title,
      description: body.description,
      ...(body.draftTurnId !== undefined ? { draftTurnId: body.draftTurnId } : {}),
      ...(body.attachmentIds !== undefined && body.attachmentIds.length > 0
        ? { attachmentIds: [...body.attachmentIds] }
        : {}),
    };
    return normalizeTicket(await request<WireTicket>('POST', BASE, { json }));
  },
  /** POST /v1/me/tickets/{number}/updates — the person's reply, with their own uploads. */
  async reply(
    number: number,
    body: string,
    attachmentIds: readonly number[] = [],
  ): Promise<Ticket> {
    const json: MemberTicketUpdateRequest = {
      body,
      ...(attachmentIds.length > 0 ? { attachmentIds: [...attachmentIds] } : {}),
    };
    return normalizeTicket(
      await request<WireTicket>('POST', `${BASE}/${number}/updates`, { json }),
    );
  },
  /** POST /v1/me/tickets/{number}/close — a support case only (`409` on a feature request). */
  async close(number: number): Promise<Ticket> {
    return normalizeTicket(await request<WireTicket>('POST', `${BASE}/${number}/close`));
  },
  /** GET /v1/me/tickets/{number}/attachments/{id} — the file (bearer only, so a Blob). */
  async downloadAttachment(number: number, attachmentId: number): Promise<DownloadResult> {
    return download(`${BASE}/${number}/attachments/${attachmentId}`, { accept: '*/*' });
  },
};
